import { createSearchParams, useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import useMediaQuery from '@mui/material/useMediaQuery'
import InputAdornment from '@mui/material/InputAdornment'
import theme from 'lib/theme'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { OrderSearchIcon } from './OrderSearchBar.styled'

const getParamKey = (key: string) => {
  const { t } = useTranslation()
  switch (key) {
    case 'customer_name':
      return t('searchContent.customerName')
    case 'customer_email':
      return t('searchContent.customerEmail')
    case 'place_id':
      return t('searchContent.place')
    case 'visible_id':
      return t('searchContent.visibleId')
    case 'order_id':
      return t('searchContent.orderId')
    case 'part_id':
      return t('searchContent.partId')
    case 'status':
      return t('status')
    default:
      return ''
  }
}

type OrderSearchParamsProps = {
  hover: boolean
  queries: { [key: string]: string } | null
  dateRange: string[]
  placeInfo: { label: string; value: string }[]
}

const orderStatuses = {
  '0': 'init',
  '1': 'open',
  '3': 'processing',
  '5': 'delivered',
  '6': 'cancelled',
}

export const OrderSearchParams = ({
  hover,
  queries,
  dateRange,
  placeInfo,
}: OrderSearchParamsProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as {
    placeName: string
    placeQuery?: string
  }
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const getValue = (key: string) => {
    if (!queries) return ''
    if (key === 'place_id') {
      return (
        placeInfo.find((place) => place.value === queries[key])?.label || ''
      )
    }
    if (key === 'status') {
      const status = orderStatuses[queries[key] as keyof typeof orderStatuses]
      return t(`orderStatus.${status}`)
    }
    return queries[key]
  }

  const handleDeleteSearchParam = (key: string) => {
    if (queries) {
      const obj = { ...queries }
      delete obj[key]
      const formattedParams = createSearchParams({
        start_delivery_date: dateRange[0],
        end_delivery_date: dateRange[1],
        page: '1',
        ...obj,
      })

      navigate(`/order?${formattedParams}`, {
        state: locationState,
      })
    }
  }
  return (
    <InputAdornment position='start'>
      {queries ? (
        <Box
          display='flex'
          flexDirection={isMobile ? 'column' : 'row'}
          sx={{ ...(isMobile && { position: 'absolute', top: 0 }) }}>
          {Object.keys(queries).map((key) => (
            <Box key={key} display='flex' ml={0.5} mb={isMobile ? 0.5 : 0}>
              <Box
                sx={{
                  padding: theme.spacing(1),
                  borderRadius: '4px 0 0 4px',
                  backgroundColor: theme.palette.secondary.main,
                }}>
                {getParamKey(key)}
              </Box>
              <Box
                sx={{
                  padding: theme.spacing(1),
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.commonColors.white,
                  borderRadius: '0 4px 4px 0',
                  display: 'flex',
                }}>
                <Box
                  sx={{
                    maxWidth: theme.spacing(10),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                  {getValue(key)}
                </Box>
                <IconButton
                  onClick={() => handleDeleteSearchParam(key)}
                  sx={{ padding: 0, marginLeft: theme.spacing(1) }}
                  aria-label={t('deleteSearchParam')}>
                  <CloseIcon
                    sx={{
                      fill: theme.palette.commonColors.white,
                      width: '0.7em',
                      height: '0.7em',
                    }}
                  />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <OrderSearchIcon aria-hidden hover={hover} />
      )}
    </InputAdornment>
  )
}
